/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable no-inline-comments, quote-props */
import { defineAsyncComponent } from 'vue'

/**
 * Get async components and imports.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion' */'~async/accordion/accordion') }),
    'announcement-bar': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.announcement-bar' */'~async/announcement-bar/announcement-bar') }),
    'boost-drawer-mobile': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.boost-drawer-mobile' */'~async/boost-drawer-mobile/boost-drawer-mobile') }),
    'before-after': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.before-after' */'~async/before-after/before-after') }),
    'black-friday-lock': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.black-friday-lock' */'~async/black-friday-lock/black-friday-lock') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers-account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers-activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers-addresses/customers-addresses') }),
    'custom-html': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.custom-html' */'~async/custom-html/custom-html') }),
    'customers-login-b2b': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login-b2b' */'~async/customers-login-b2b/customers-login-b2b') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers-login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers-order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers-orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers-register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers-reset-password/customers-reset-password') }),
    'doorway': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.doorway' */'~async/doorway/doorway') }),
    'downloads-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.downloads-accordion' */'~async/downloads-accordion/downloads-accordion') }),
    'featured-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-products' */'~async/featured-products/featured-products') }),
    'job-detail': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.job-detail' */'~async/job-detail/job-detail') }),
    'job-listings': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.job-listings' */'~async/job-listings/job-listings') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'media-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.media-carousel' */'~async/media-carousel/media-carousel') }),
    'mega-menu': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.mega-menu' */'~async/mega-menu/mega-menu') }),
    'pill-navigation': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.pill-navigation' */'~async/pill-navigation/pill-navigation') }),
    'product-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-carousel' */'~async/product-carousel/product-carousel') }),
    'product-tech-spec': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-tech-spec' */'~async/product-tech-spec/product-tech-spec') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer/site-footer') }),
    'split-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.split-section' */'~async/split-section/split-section') }),
    'sticky-media-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.sticky-media-banner' */'~async/sticky-media-banner/sticky-media-banner') }),
    'store-locator': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.store-locator' */'~async/store-locator/store-locator') }),
    'teaser-modal-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.teaser-modal-section' */'~async/teaser-modal-section/teaser-modal-section') }),
    'text': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text' */'~async/text/text') }),
    'text-icons': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-icons' */'~async/text-icons/text-icons') }),
    'transition-height': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.transition-height' */'~async/transition-height/transition-height') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components here.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import BoostAnalytics from '~global/boost-analytics/boost-analytics'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import MarketSwitcher from '~global/market-switcher/market-switcher'
import MarketSwitcherData from '~global/market-switcher-data/market-switcher-data'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SiteHeader from '~global/site-header/site-header'
import VideoPlayer from '~global/video-player/video-player'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'boost-analytics': BoostAnalytics,
    'boost-api-call': BoostApiCall,
    'btn': Btn,
    'index': Index,
    'market-switcher': MarketSwitcher,
    'market-switcher-data': MarketSwitcherData,
    'overlay': Overlay,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    'video-player': VideoPlayer,
    // canvas-global-object-end
  }
}

/**
 * Import Vuex stores here.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import MainCollectionStore from '~stores/main-collection'
import MarketSwitcherStore from '~stores/market-switcher'
import OverlaysStore from '~stores/overlays'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'main-collection': MainCollectionStore,
    'market-switcher': MarketSwitcherStore,
    'overlays': OverlaysStore,
    // canvas-stores-object-end
  }
}
